<!-- A quick briefing on this web app: 

SmoothRide - Codename "InfrastructureGPT" is a platform that allows cycling citizens to upload experiences from their rides
Experiences can be nuisances or positive examples they would like to see in more places.
Assisted by AI users can then ideate on their submission and help their region improve their cycling infrastructure.
This project is carried out by Phil Wornath in the course of his master's project. Data is used for research purposes only in a pseudonomized nature.

ImageUpload Process: 
- User registers and logs in (Optional)
- User uploads an image
- Geo-Location is extracted from the image
- an image detection algoritm detects objects in the provided image
- Users can provide additional labels to train the image detection model
- User adds comment to the submission for further context
- AI generates a list of possible improvements
- User can rate the suggestions or generate new ones
- User can reflect to the suggestions and propose a solution
- user can submit the report

In the ExploreFeed view, users can view
- Receive content recommendations per submission
- a city overview with the most reported issues in comparison to other cities
- the submissions on a map

Technology Stack:
- Frontend based on Vue3, 
- Backend based on Flask / Python, OpenCV, PyTorch, YOLOV8, a generative AI model and a custom backend based on Flask / Python
- Data stored in a self-hosted PocketBase instance hosted in Germany
- Users are in charge of their own data which can be deleted autonomously at any time using the "Delete Account" button in the user dashboard
 -->

<template>
  <div class="app">
    <!-- <MapComponent class="map-background" /> -->
    <a class="skip-link" href="#main-content" @click.prevent="skipToMain">{{ $t('common.skipToMain') }}</a>
    <HeaderSection />
    <ErrorBanner :errorMessage="error" />
    <main id="main-content" class="page" :class="`page--${$route.meta.layout || 'narrow'}`" tabindex="-1">
      <router-view :key="$route.name === 'ViewSubmission' ? $route.params.id : undefined" />
    </main>
    <SiteFooter />
    <BottomNavBar />
  </div>
</template>

<script>
import HeaderSection from "@/components/HeaderSection.vue";
import ErrorBanner from "@/components/ErrorBanner.vue";

// import SubmissionForm from "@/views/SubmissionForm.vue";
// import MapComponent from "@/components/MapComponent.vue";
// import FooterSection from "@/components/FooterSection.vue";
import BottomNavBar from "@/components/BottomNavBar.vue";
import SiteFooter from "@/components/SiteFooter.vue";

export default {
  name: "App",
  components: {
    HeaderSection,
    SiteFooter,
    // SubmissionForm,
    // MapComponent,
    // FooterSection,
    ErrorBanner,
    BottomNavBar,
  },

  data() {
    // Define data properties
    return {
      error: null,
    };
  },
  methods: {
    skipToMain() {
      const main = document.getElementById("main-content");
      if (main) {
        main.focus();
        main.scrollIntoView();
      }
    },
  },
  watch: {
    // After a page change, move focus to the new content so keyboard and
    // screen reader users do not stay on the old navigation link.
    $route() {
      this.$nextTick(() => {
        const main = document.getElementById("main-content");
        if (main) main.focus({ preventScroll: true });
      });
    },
  },
};
</script>

<style>
:root {
  /* Main color */
  --color-main: #41c4ab;
  --color-not-just-white: #fcfcfc;
  /* Illustration tokens (components/scenes, HillScene). Light values match the original artwork. */
  --ill-ink: #282828;      /* outlines, trails, rider */
  --ill-line: #1a1a1a;     /* heavy strokes (phone frame) */
  --ill-steel: #585858;    /* handlebar, tree trunk, hand */
  --ill-mint: #41c4ab;
  --ill-mint-soft: #71dfbf;
  --ill-paper: #ffffff;    /* white parts, arrows */
  --ill-surface: #f1f1f1;  /* phone screen */
  --ill-note: #282828;     /* report note card */
  --ill-note-text: #ffffff;
  --ill-accent: #e5533d;   /* map pin */
  --ill-spark: #f7c948;
  --ill-wand: #f1e2c4;
  --ill-chip: #df7191;     /* model chips in the loop */

  /* Primary Colors */
  --color-primary: var(--color-main); /* brand mint, used for surfaces */
  --color-primary-light: #88e2d1;
  --color-primary-dark: #1e9b87;
  --color-on-primary: #0b2420; /* text on mint: 7.5:1 (white on mint is only 2.1:1) */
  --color-primary-text: #14796a; /* mint-family text on light backgrounds: 5.3:1 */
  --color-focus: #0b57d0;
  /* Status and selected states (Figma: color/status/*, color/border/selected). Mint alone is only 2.2:1 on white,
     so progress, done steps and selected outlines use this darker teal (5.3:1). */
  --color-status-done: #14796a;
  --color-on-status-done: #ffffff;
  --color-border-selected: #14796a;

  /* Fresh Mint 2030 tokens (match the Figma design system) */
  --color-surface: #ffffff;
  --color-subtle: #e6f8f4; /* mint tint for chips and stat tiles */
  --color-border: #dbe7e4;
  --color-border-input: #767676; /* 4.5:1 on white */
  --radius-sm: 5px;
  --radius-md: 10px;
  --radius-lg: 16px;
  --radius-xl: 24px;
  --radius-pill: 30px;
  --shadow-card: 0 1px 2px rgba(11, 36, 32, 0.04), 0 6px 20px -8px rgba(11, 36, 32, 0.10);
  --shadow-card-hover: 0 2px 4px rgba(11, 36, 32, 0.05), 0 14px 28px -10px rgba(11, 36, 32, 0.16);
  --shadow-floating: 0 2px 6px rgba(11, 36, 32, 0.06), 0 16px 36px -12px rgba(11, 36, 32, 0.22);
  --font-mono: "Roboto Mono", ui-monospace, SFMono-Regular, Menlo, monospace;
  --touch-min: 48px;

  /* Page layout tokens. One place decides the side margins of the whole app.
     --page-gutter: minimum space between content and the screen edge
     --content-narrow / --content-wide: max width of the content column
     --page-inset: the resulting space left and right of the column (also used by the header)
     A route picks its column with meta.layout: 'narrow' (default), 'wide' or 'full' (edge to edge). */
  --page-gutter: 16px;
  --content-narrow: 720px;
  --content-wide: 1120px;
  --content-max: var(--content-narrow);

  /* Block width scale: how wide one content block may grow inside a page column.
     xs 360 / sm 480 / md 640 (default) / lg 880 / xl 1120 / bleed = edge to edge.
     Use it as <section class="block block--lg">. Every step shrinks to the screen on small viewports. */
  --w-xs: 360px;
  --w-sm: 480px;
  --w-md: 640px;
  --w-lg: 880px;
  --w-xl: 1120px;
  --page-inset: max(var(--page-gutter), calc((100vw - var(--content-max)) / 2));
  --color-primary-transparent: rgba(65, 196, 171, 0.15);
  --color-disabled: #dddddd; /* Figma bg/disabled */
  --color-text-disabled: #999999; /* Figma text/disabled */

  /* Complementary Colors */
  --color-complementary: #c44189;
  --color-complementary-light: #e288c7;
  --color-complementary-dark: #9b1e67;
  --color-complementary-transparent: rgba(196, 65, 137, 0.15);

  /* Tertiary Colors */
  --color-tertiary: #c4a141;
  --color-tertiary-light: #e2d188;
  --color-tertiary-dark: #9b871e;
  --color-tertiary-transparent: rgba(196, 161, 65, 0.15);

  /* Secondary Colors */
  --color-secondary: #4a5b57; /* 7:1 on white, 6.6:1 on the band */
  --color-secondary-light: #dddddd;
  --color-secondary-dark: #666666;

  /* Neutral Colors */
  /* Light theme, background option A (Figma "Content pages redesign"): white page, mint-tint bands, ink text */
  --color-text: #0b2420; /* ink, 16:1 on white */
  --color-text-light: #f1f1f1;
  --color-text-dark: #0b2420;
  --color-background: #ffffff;
  --color-background-light: #f3faf8;
  --color-band: #f3faf8; /* mint-tint section band */
  --color-background-dark: #0e1513;
  --color-background-dark-medium: #17211e;
  --color-background-dark-transparent: #0e1513e3;
  --color-stickynote: #f8e7b4;
  --color-on-stickynote: #0b2420;

  /* Always-dark surfaces (nav on the landing page): fixed values that do not follow the theme */
  --color-fixed-dark-surface: #0e1513;
  --color-fixed-dark-hover: #1f2d29;
  --color-fixed-dark-border: #2a3a36;
  --color-fixed-dark-text: #f2f2f2;
  --color-fixed-dark-text-muted: #b3b3b3;

  /* Severity (Figma bg/severity-* and text/on-severity-*) */
  --color-severity-low: #2ecc71;
  --color-severity-medium: #f39c12;
  --color-severity-high: #c0392b;
  /* Chart severity colours, >= 3:1 on white (Figma color/viz/severity-*), always paired with a symbol */
  --color-viz-severity-low: #1a7f45;
  --color-viz-severity-medium: #a35a00;
  --color-viz-severity-high: #c0392b;
  --color-on-severity-low: #0b2420;
  --color-on-severity-medium: #1a1a1a;
  --color-on-severity-high: #ffffff;

  /* Semantic text/border for errors and ratings */
  --color-error-text: #c0392b;
  --color-error-border: #c0392b;
  --color-icon-rating: #b25e00; /* Figma icon/rating */

  /* Warning Colors */
  --color-warning: #ffa500;
  --color-warning-light: #ffdab9;
  --color-warning-dark: #ff8c00;

  /* Error Colors */
  --color-error: #e74c3c;
  --color-error-light: #f2dede;
  --color-error-dark: #c0392b;
}

body {
  /* margin: 0; */
  width: auto;
  margin: 0;

  background-color: var(--color-background);
  transition: background-color 0.2s ease-in-out;

  color: var(--color-text-dark);
  font-family: "Poppins", sans-serif;

  /* font-family: Lato, sans-serif; */
  /* display: flex;
  /* justify-content: space-around; */
  align-items: center;
  text-align: center;
}

body.dark-mode {
  --ill-ink: #cfdcd8;
  --ill-line: #e3ece9;
  --ill-steel: #7f948e;
  --ill-mint: #41c4ab;
  --ill-mint-soft: #2f8f7c;
  --ill-paper: #e8f2ef;
  --ill-surface: #1b2622;
  --ill-note: #e8f2ef;
  --ill-note-text: #0b2420;
  --ill-accent: #ff7a66;
  --ill-chip: #c95a7c;
  --color-viz-severity-low: #4fd18b;
  --color-viz-severity-medium: #ffb347;
  --color-viz-severity-high: #ff8a7a;
  --color-primary-text: #41c4ab;
  --color-secondary: #b3b3b3;
  --color-focus: #ffd54a;
  --color-status-done: #41c4ab;
  --color-on-status-done: #0b2420;
  --color-border-selected: #41c4ab;
  --color-surface: #17211e;
  --color-subtle: #1f2d29;
  --color-border: #2a3a36;
  --color-border-input: #8a9a96;
  --color-text: #f2f2f2;
  --color-background: #0e1513;
  --color-background-light: #1f2d29;
  --color-band: #121b18;
  --color-disabled: #555555;
  --color-text-disabled: #b3b3b3;
  --color-error-text: #ff8a7a;
  --color-error-border: #ff8a7a;
  --color-icon-rating: #ffd54a;
  --shadow-card: 0 0 0 rgba(0, 0, 0, 0);
  --shadow-card-hover: 0 8px 24px -10px rgba(0, 0, 0, 0.6);
  --shadow-floating: 0 12px 32px -10px rgba(0, 0, 0, 0.6);
  background-color: var(--color-background-dark);
  color: var(--color-text-light);
}

/* Visible keyboard focus everywhere. Mouse clicks do not show it. */
:focus-visible {
  outline: 3px solid var(--color-focus) !important;
  outline-offset: 2px;
}

.skip-link {
  position: absolute;
  left: 8px;
  top: -64px;
  z-index: 1000;
  padding: 12px 16px;
  background: var(--color-background-dark);
  color: var(--color-text-light) !important;
  border-radius: 8px;
  font-weight: 600;
}
.skip-link:focus {
  top: 8px;
}
main#main-content:focus {
  outline: none !important;
}

/* Static text version written by scripts/prerender.js; Vue replaces it on mount. */
.prerendered { max-width: 680px; margin: 0 auto; padding: 16px; text-align: left; }
.prerendered :is(h1, h2, h3, h4, p, li) { text-align: left; }

/* Buttons do not inherit the page font by default (they showed in the system sans) */
button, input, select, textarea { font-family: inherit; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.link-button {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: var(--color-primary-text);
  text-decoration: underline;
  cursor: pointer;
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}


.dark-mode svg {
  fill: var(--color-text-light);
  stroke: var(--color-text-light);
}

p.fineprint {
  font-size: smaller;
  color: var(--color-secondary);
}

a {
  text-decoration: none;
  color: var(--color-primary-text);
}
.dark-mode a {
  color: var(--color-text-light);
}

.dark-mode h1 {
  color: var(--color-text-light);
}

h1 {
  font-weight: 400;
  margin-bottom: 12px;
  padding: 0;
  margin-top: 0;
  text-align: center;
  font-size: 1.5em;
  color: var(--color-text);
}

h2 {
  font-weight: 400;
  margin-bottom: 12px;
  padding: 0;
  margin-top: 0;
  text-align: center;
  /* color: var(--color-text); */
}

.dark-mode h2 {
  color: var(--color-text-light);
}

h3 {
  color: var(--color-text);
}

.dark-mode h3 {
  color: var(--color-text-light);
}
/* Define the animation for the underline */
@keyframes slide-in {
  0% {
    width: 0;
    opacity: 0;
  }
  100% {
    width: 100%;
    opacity: 1;
  }
}

/* Apply the animation to the .underline-primary class */
.underline-primary {
  position: relative;
  text-decoration: none;
}

.underline-primary::before {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0;
  height: 2px;
  background-color: var(--color-primary);
  animation: slide-in 1.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.app {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  /* align-items: center; */
  /* box-sizing: border-box; */
  /* height: 100vh; */
  margin: auto;
  /* max-width: 800px; */
}

/* Lists of cards (Explore, dashboard): one ul, 12 px between items */
.report-section { display: flex; flex-direction: column; gap: 12px; }
.report-section h2 { margin: 0; font-size: 18px; }
.report-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 12px; }

/* Layout logic:
   body        page background (the only big surface)
   .app        centred column, max 800 px
   main.page   gutter + space under the floating nav
   .view       the root of one screen: a plain vertical stack, no box of its own
   .card       a real group of content (report, settings section, note): surface, border, radius */
.view {
  display: flex;
  flex-direction: column;
  gap: 24px;
  animation: fade-in 0.5s ease-out;
}

.card {
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-card);
  padding: 20px;
  text-align: left;
}

.auth-form input[type="text"][data-v-3a5d5944],
.auth-form input[type="password"][data-v-3a5d5944] {
  padding: 12px;
  border: none;
  margin: 8px 0;
  border-bottom: 1px solid #ccc;
  font-size: 18px;
  background: var(--ol-background-color);
  border-radius: 4px;
}


/* list of single-purpose classes  */
.fade-in {
  animation: fade-in 0.5s ease-out;
}

.complementary {
  background-color: var(--color-complementary) !important;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* .dark-mode exif-info{
  color: var(--color-text-light);
} */

.dark-mode .form {
  background-color: var(--color-secondary-dark);
}

.dark-mode .file-picker-label {
  border-color: #ccc;
}

.dark-mode .comment textarea,
.dark-mode div.labels > form > input[type="text"] {
  background-color: #1f2d29;
  color: white;
  border-color: #2a3a36;
}

.error-section {
  background-color: #ffcccc;
  border: 1px solid #ff0000;
  padding: 10px;
  margin-top: 10px;
}

.btn-cta {
  background-color: var(--color-primary);
  color: var(--color-on-primary);
  font-size: 16px;
  font-weight: 600;
  min-height: var(--touch-min);
  padding: 14px 28px;
  border-radius: var(--radius-pill);
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.btn-cta:hover {
  background-color: var(--color-primary-dark);
}

.btn-cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-standard {
  background-color: transparent;
  color: var(--color-text);
  font-size: 16px;
  font-weight: 600;
  min-height: var(--touch-min);
  border: 2px solid var(--color-border-input);
  padding: 14px 28px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  transition: background-color 0.2s ease;
}
.btn-standard:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-standard:hover {
  background-color: var(--color-subtle);
}

.dark-mode .btn-standard {
  color: var(--color-text-light);
}

/* Icon button, Option B "Outline" (Figma: Components > Icon button).
   White circle, 2 px outline, dark icon. Hover turns outline and icon green on a light tint,
   Selected is solid mint. No shadow. Focus ring comes from the global :focus-visible rule. */
.btn-round {
  width: var(--touch-min);
  height: var(--touch-min);
  box-sizing: border-box;
  border-radius: 50%;
  background-color: var(--color-surface);
  color: var(--color-text);
  cursor: pointer;
  font-size: 24px;
  border: 2px solid var(--color-border-input);
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.btn-round:hover:not(:disabled) {
  background-color: var(--color-background-light);
  border-color: var(--color-primary-text);
  color: var(--color-primary-text);
}

/* Selected: an active toggle such as an upvote. Prefer aria-pressed="true" in the markup. */
.btn-round.selected,
.btn-round[aria-pressed="true"],
.btn-round.selected:hover:not(:disabled),
.btn-round[aria-pressed="true"]:hover:not(:disabled) {
  background-color: var(--color-primary);
  border-color: var(--color-border-selected);
  color: var(--color-on-primary);
}

.btn-round:disabled {
  background-color: var(--color-surface);
  border-color: var(--color-border);
  color: var(--color-text-disabled);
  cursor: not-allowed;
}

/* Dark mode: Figma Semantic color, Dark mode values. */
.dark-mode .btn-round {
  background-color: #17211e;
  border-color: #8a9a96;
  color: #f2f2f2;
}
.dark-mode .btn-round:hover:not(:disabled) {
  background-color: #1f2d29;
  border-color: var(--color-primary);
  color: var(--color-primary);
}
.dark-mode .btn-round.selected,
.dark-mode .btn-round[aria-pressed="true"] {
  background-color: var(--color-primary);
  border-color: var(--color-border-selected);
  color: var(--color-on-primary);
}
.dark-mode .btn-round:disabled {
  background-color: #17211e;
  border-color: #2a3a36;
  color: #b3b3b3;
}
.map-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
}

/* One page column: main owns the horizontal gutter and the space under the floating nav,
   so views and cards no longer need their own outer margins. */
.page {
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
  /* side margins from the layout tokens; --page-inset is recomputed here so a --content-max override applies */
  --page-inset: max(var(--page-gutter), calc((100vw - var(--content-max)) / 2));
  padding-inline: var(--page-inset);
  /* Bottom room for the floating nav is kept by the SiteFooter below main. */
  padding-bottom: 24px; /* the footer below keeps the room for the floating nav */
}
.page--wide {
  --content-max: var(--content-wide);
}
/* Edge to edge (landing page): no side margins and no space reserved for the nav. */
.page--full {
  --page-inset: 0px;
  padding-bottom: 0;
}
/* A child that should run edge to edge inside a padded page (hero images, tinted bands). */
.bleed {
  margin-inline: calc(var(--page-inset) * -1);
}
/* Block width scale (sets --block-w; .block reads it, default md). Works in any layout. */
.block--xs { --block-w: var(--w-xs); }
.block--sm { --block-w: var(--w-sm); }
.block--md { --block-w: var(--w-md); }
.block--lg { --block-w: var(--w-lg); }
.block--xl { --block-w: var(--w-xl); }
.block--bleed { --block-w: none; width: 100vw; margin-inline: calc(50% - 50vw); }

.success-metric {
  background-color: var(--color-primary);
  color: var(--color-on-primary);
  padding: 24px;
  border-radius: 16px;
  display: flex;
  box-shadow: var(--shadow-card);
  flex-direction: column;
  align-items: center;
}
.success-metric h2 {
  margin-top: 0;
}

.success-metric p {
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  gap: 20px;
}
.success-metric a {
  color: var(--color-on-primary);
  /* bold font and underlined*/
  font-weight: 700;
  text-decoration: underline;
}

.comment textarea {
  border: none;
  border-radius: 12px;
  padding: 20px;
  font-size: 16px;
  font-family: "Montserrat", sans-serif;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  background-color: var(--color-surface);
  transition: all 0.5s ease-in-out;
}

.comment textarea:focus {
  outline: none;
  box-shadow: 0px 0px 32px rgba(0, 0, 0, 0.1);
  background-image: linear-gradient(
    45deg,
    rgba(0, 187, 255, 0.1),
    rgba(0, 0, 0, 0.1),
    rgba(0, 0, 0, 0.05)
  );
  /* animation: spin 2s linear infinite; */
}

@media only screen and (max-width: 768px) {
  h1 {
    font-size: 1.2em;
  }
  h2 {
    font-size: 1.2em;
  }
  .view {
    gap: 16px;
  }
}
</style>
